// AiSummary — the AI executive summary on the Report page. The summary + per-section reasoning are
// PRE-GENERATED server-side once per data snapshot (by the AiSummaryWarmer) and cached, so this
// component READS them — viewing never costs tokens. Admin/client can EDIT the summary text itself
// (an override that everyone then sees) and edit the human COMMENT. Layout:
//   • Summary view (default for all) → key-points bullets + the team COMMENT (read by all; edited by
//     admin/client here). Admin/client can also edit the bullet text.
//   • Detailed view → the full AI analysis; admin/client can edit it too. No comment here.
//   • Admin/client also get a per-section "how the AI reached this" reasoning expander.

import { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { InsightsApi } from "../hooks/useApi.js";
import { useAuth } from "../auth/AuthProvider.jsx";
import Icon from "./Icon.jsx";

const POLL_MS = 12000;   // while pending, re-check every 12s…
const MAX_POLLS = 6;     // …up to ~72s (warmer debounce ~30s + generation).

// onGenerated(text) fires once a summary is available, so the Report can include it in a PDF export.
export default function AiSummary({ runId, onGenerated }) {
  const { user } = useAuth();
  const role = user?.role;
  const isTenant = role === "tenant";
  const isEditor = role === "admin" || role === "client";
  const canViewAi = user?.canViewAiSummary === true;   // admin/client always true; tenants only if granted

  const [state, setState] = useState({ status: "loading" });   // loading | pending | done | error
  const [open, setOpen] = useState(true);            // expand/collapse the whole section
  const [view, setView] = useState("summary");       // summary (bullets) | detailed (full) — Summary first for everyone
  const [showReasoning, setShowReasoning] = useState(false);
  const [draft, setDraft] = useState("");            // comment textarea
  const [save, setSave] = useState({ busy: false, ok: false, err: null });
  const [editing, setEditing] = useState(null);      // null | "summary" | "detailed" — inline text override
  const [textDraft, setTextDraft] = useState("");
  const [textSave, setTextSave] = useState({ busy: false, err: null });
  const pollRef = useRef({ timer: null, count: 0 });
  const loadRef = useRef(null);
  const inited = useRef(false);   // seed the comment draft from the server just once

  const clearPoll = () => { if (pollRef.current.timer) { clearTimeout(pollRef.current.timer); pollRef.current.timer = null; } };

  const load = useCallback(async () => {
    try {
      const r = await InsightsApi.aiSummary(runId);
      if (!inited.current) { setDraft(r.comments ?? ""); inited.current = true; }
      if (r.status === "ready") {
        clearPoll();
        setState({ status: "done", summary: r.summary, bullets: r.bullets, reasoning: r.reasoning,
          edited: r.edited, editedBy: r.editedBy, editedAt: r.editedAt,
          comments: r.comments, commentsUpdatedAt: r.commentsUpdatedAt, model: r.model, generatedAt: r.generatedAt });
        onGenerated?.(r.summary);
      } else {
        setState({ status: "pending", comments: r.comments, commentsUpdatedAt: r.commentsUpdatedAt });
        if (pollRef.current.count < MAX_POLLS) {
          pollRef.current.count += 1;
          clearPoll();
          pollRef.current.timer = setTimeout(() => loadRef.current?.(), POLL_MS);
        }
      }
    } catch (e) {
      clearPoll();
      setState({ status: "error", error: e.body?.error ?? e.message });
    }
  }, [runId, onGenerated]);
  useEffect(() => { loadRef.current = load; }, [load]);

  useEffect(() => {
    pollRef.current.count = 0;
    inited.current = false;
    setState({ status: "loading" });   // eslint-disable-line react-hooks/set-state-in-effect
    load();
    return clearPoll;
  }, [load]);

  const saveComment = async () => {
    setSave({ busy: true, ok: false, err: null });
    try {
      const r = await InsightsApi.aiSummaryComments(runId, draft.trim());
      setState((s) => ({ ...s, comments: r.comments, commentsUpdatedAt: r.commentsUpdatedAt }));
      setSave({ busy: false, ok: true, err: null });
    } catch (e) {
      setSave({ busy: false, ok: false, err: e.body?.error ?? e.message });
    }
  };

  // ---- editable summary text (admin/client override; everyone then sees it) ----
  const startEdit = (which, current) => { setEditing(which); setTextDraft(current ?? ""); setTextSave({ busy: false, err: null }); };
  const cancelEdit = () => { setEditing(null); setTextSave({ busy: false, err: null }); };
  const saveText = async () => {
    setTextSave({ busy: true, err: null });
    try {
      await InsightsApi.aiSummaryEdit(runId, editing === "summary" ? { bullets: textDraft } : { summary: textDraft });
      setEditing(null);
      await load();   // refetch the effective (edited) text + flags
    } catch (e) {
      setTextSave({ busy: false, err: e.body?.error ?? e.message });
    }
  };
  const revertText = async () => {
    setTextSave({ busy: true, err: null });
    try { await InsightsApi.aiSummaryEditReset(runId); setEditing(null); await load(); }
    catch (e) { setTextSave({ busy: false, err: e.body?.error ?? e.message }); }
  };

  const when = state.generatedAt ? new Date(state.generatedAt).toLocaleString("en-ZA") : "";
  const hasComment = !!(state.comments && state.comments.trim());

  const spinner = (text) => (
    <p className="hint" style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span className="spinner" style={{ color: "var(--primary)" }} /> {text}
    </p>
  );

  // Tenants without the "view AI summary" permission don't see the card at all.
  if (isTenant && !canViewAi) return null;

  const renderBody = () => {
    if (state.status === "loading") return spinner("Loading the executive summary…");
    if (state.status === "pending") return spinner("The executive summary is being prepared from the latest data and will appear here shortly.");
    if (state.status === "error") return <div className="error"><Icon name="alert" size={15} /> {state.error}</div>;

    const hasBullets = !!(state.bullets && state.bullets.trim());
    const detailed = view === "detailed" || !hasBullets;   // fall back to detailed prose when no bullets
    const onSummary = !detailed || !hasBullets;            // is the Summary (key points) the visible view?
    const bodyText = detailed ? state.summary : state.bullets;
    const editKey = detailed ? "detailed" : "summary";

    return (
      <>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
          {hasBullets ? (
            <div className="seg sm">
              <button type="button" className={!detailed ? "on" : ""} onClick={() => { setView("summary"); setEditing(null); }}><Icon name="spark" size={14} /> Summary</button>
              <button type="button" className={detailed ? "on" : ""} onClick={() => { setView("detailed"); setEditing(null); }}><Icon name="report" size={14} /> Detailed</button>
            </div>
          ) : <span />}
          {isEditor && !editing && (
            <button type="button" className="btn ghost sm" onClick={() => startEdit(editKey, bodyText)}>
              <Icon name="edit" size={13} /> Edit {detailed ? "detailed" : "summary"}
            </button>
          )}
        </div>

        {editing === editKey ? (
          <div className="field">
            <textarea className="input" rows={detailed ? 16 : 8} value={textDraft}
              onChange={(e) => setTextDraft(e.target.value)} style={{ resize: "vertical", width: "100%", minWidth: 0, fontFamily: "inherit" }} />
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 6 }}>
              <button type="button" className="btn primary sm" onClick={saveText} disabled={textSave.busy}>{textSave.busy ? "Saving…" : "Save"}</button>
              <button type="button" className="btn ghost sm" onClick={cancelEdit} disabled={textSave.busy}>Cancel</button>
              {state.edited && <button type="button" className="btn ghost sm" onClick={revertText} disabled={textSave.busy}>Revert to AI</button>}
              {textSave.err && <span className="hint" style={{ color: "var(--neg)" }}>{textSave.err}</span>}
              <span className="hint">Markdown — saved for all viewers on this data snapshot.</span>
            </div>
          </div>
        ) : (
          <div className="ai-prose"><ReactMarkdown>{bodyText}</ReactMarkdown></div>
        )}

        {state.edited && editing !== editKey && (
          <p className="hint" style={{ marginTop: 8 }}>
            <Icon name="edit" size={12} /> Edited{state.editedBy ? ` by ${state.editedBy}` : ""}
            {state.editedAt ? ` · ${new Date(state.editedAt).toLocaleString("en-ZA")}` : ""}
            {isEditor && <> · <button type="button" onClick={revertText}
              style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "var(--primary)", font: "inherit", textDecoration: "underline" }}>revert to AI</button></>}
          </p>
        )}

        <p className="hint" style={{ marginTop: 14 }}>
          Generated {when ? `${when} ` : ""}by {state.model} from aggregated figures — customer &amp; salesperson
          names are anonymised before analysis. AI-generated; verify before acting.
        </p>

        {/* COMMENT — Summary view only (item 5): read by everyone, edited by admin/client, right here. */}
        {onSummary && (
          <div style={{ marginTop: 16, borderTop: "1px solid var(--line)", paddingTop: 14 }}>
            {hasComment && (
              <div className="ai-prose" style={{ whiteSpace: "pre-line", marginBottom: isEditor ? 12 : 0 }}>{state.comments}</div>
            )}
            {isEditor && (
              <div className="field">
                <label>Comment shown to tenant users (on the Summary)</label>
                <textarea className="input" rows={3} value={draft}
                  onChange={(e) => { setDraft(e.target.value); setSave({ busy: false, ok: false, err: null }); }}
                  placeholder="Add a note for tenant users… (clears when new data is ingested)" style={{ resize: "vertical", minWidth: 0, width: "100%" }} />
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 4 }}>
                  <button type="button" className="btn primary sm" onClick={saveComment} disabled={save.busy}>{save.busy ? "Saving…" : "Save comment"}</button>
                  {save.ok && <span className="hint">Saved — tenants now see this.</span>}
                  {save.err && <span className="hint" style={{ color: "var(--neg)" }}>{save.err}</span>}
                  {state.commentsUpdatedAt && !save.ok && <span className="hint">Last updated {new Date(state.commentsUpdatedAt).toLocaleString("en-ZA")}</span>}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Admin/client only: per-section AI reasoning */}
        {isEditor && state.status === "done" && state.reasoning && (
          <div style={{ marginTop: 14 }}>
            <button type="button" className="btn ghost sm" onClick={() => setShowReasoning((v) => !v)} aria-expanded={showReasoning}>
              <Icon name={showReasoning ? "up" : "down"} size={13} /> {showReasoning ? "Hide" : "Show"} how the AI reached this
            </button>
            {showReasoning && (
              <div className="ai-prose" style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid var(--line)" }}>
                <ReactMarkdown>{state.reasoning}</ReactMarkdown>
              </div>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="viz-card">
      <div className="vc-head">
        <div>
          <h3><Icon name="spark" size={16} /> AI insights · executive summary</h3>
          <div className="sub">
            {isTenant
              ? "Your team's summary of the latest snapshot"
              : "A plain-language read of the snapshot — generated automatically when the data refreshes"}
          </div>
        </div>
        <button type="button" className="btn ghost sm" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          <Icon name={open ? "up" : "down"} size={13} /> {open ? "Hide" : "Show"}
        </button>
      </div>
      {open && renderBody()}
    </div>
  );
}
